import React, { useContext, useState, useMemo } from 'react';
import { func, object } from 'prop-types';
import { Box, Card } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  Typography, Button, InputField, useError,
} from '@avon/component-library';
import cookies from 'react-cookies';
import CircularProgress from '@material-ui/core/CircularProgress';
import { isMobile, isDesktop } from '../../utils/getDeviceType';
import HomeContext from '../../home/HomeContext';
import successSvg from '../../styles/assets/success.svg';
import backBlackSvg from '../../styles/assets/backBlack.svg';
import { saveCloseAccount } from '../../utils/api/endpoints';
import CharCount from './CharCount';

const useStyles = makeStyles(theme => ({
  main: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: '0 140px',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      padding: '0 90px',
    },
    [theme.breakpoints.down('xs')]: {
      margin: '0 auto',
      padding: '0 18px',
    },
  },
  noticeBox: {
    backgroundColor: '#F6F9F9',
    padding: 16,
    margin: '40px auto',
    borderRadius: '8px',
    [theme.breakpoints.down('sm')]: {
      margin: '24px auto',
    },
  },
  cardBox: {
    padding: 32,
    marginTop: 32,
    boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.14), inset 0px -4px 0px #7F28C4',
    borderRadius: '8px',
    marginBottom: 40,
  },
  back: {
    position: 'absolute',
    top: 20,
    left: cookies.load('langCd') === 'ar' ? 'unset' : 24,
    right: cookies.load('langCd') === 'ar' ? 24 : 'unset',
    fontWeight: 700,
    fontSize: 16,
    textTransform: 'uppercase',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      left: cookies.load('langCd') === 'ar' ? 'unset' : 9,
      right: cookies.load('langCd') === 'ar' ? 9 : 'unset',
      top: 18,
    },
  },
  closeSent: {
    width: 468,
    margin: '0 auto 24px',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      padding: '0 26px',
    },
  },
  icon: {
    width: 64,
    height: 64,
    margin: '66px auto 30px',
  },
  button: {
    marginBottom: 48,
    minHeight: 78,
    [theme.breakpoints.down('sm')]: {
      marginTop: 42,
      bottom: 90,
      minHeight: 'unset',
    },
    [theme.breakpoints.down('xs')]: {
      left: '50%',
      marginLeft: '-33px',
      // width: 'unset',
      position: 'absolute',
      bottom: 48,
      marginBottom: 0,
      minHeight: 'unset',
    },
  },
  progress: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 48,
    minHeight: 78,
    [theme.breakpoints.down('sm')]: {
      marginTop: 42,
      minHeight: 'unset',
    },
    [theme.breakpoints.down('xs')]: {
      marginBottom: 20,
      minHeight: 'unset',
    },
  },
  closeButton: {
    marginBottom: 48,
    minHeight: 78,
    [theme.breakpoints.down('sm')]: {
      marginTop: 42,
      minHeight: 'unset',
    },
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
      bottom: 20,
      left: '50%',
      marginLeft: '-80px',
      marginBottom: 0,
      minHeight: 'unset',
    },
  },
  mobileReason: {
    marginTop: 34,
  },
  limited: {
    display: 'block',
    '& textarea': {
      fontSize: '14px',
      fontFamily: 'Montserrat',
      color: theme.palette.colors.textDark,
    },
  },
  title: {
    width: 367,
    margin: 'auto',
    [theme.breakpoints.down('sm')]: {
      width: 198,
    },
    [theme.breakpoints.down('xs')]: {
      width: 239,
    },

  },
}));

const CloseAccount = ({ callApi, config, redirectTo }) => {
  const classes = useStyles();
  const [, dispatch] = useContext(HomeContext);
  const [closed, setClosed] = useState(false);
  const [reason, setReason] = useState('');
  const { properties } = config.fields;
  const { setError, cleanToastBanner } = useError();
  const [isSubmitting, setSubmitting] = useState(false);

  const handleClose = async () => {
    setSubmitting(true);
    try {
      await callApi(saveCloseAccount(reason)).then((res) => {
        setClosed(true);
        console.log(res);
      });
      cleanToastBanner();
      setSubmitting(false);
      return true;
    } catch (e) {
      setError(e.error);
      setSubmitting(false);
      return false;
    }
  };

  const handleChange = (e) => {
    if (e.target.value.trim().length <= 200) {
      setReason(e.target.value);
    }
  };

  const endAdornment = useMemo(() => (
    <CharCount
      current={reason.trim().length}
      limit={200}
    />
  ), [reason], 200);

  return (
    <Box className={classes.main}>
      <Box
        className={classes.back}
        onClick={
          () => {
            dispatch({ currentPage: 'home' });
            redirectTo({
              type: 'home', subType: null, hasFooter: false, params: null,
            });
          }}
      >
        <img src={backBlackSvg} alt="back-to-home-icon" style={{ marginRight: 10 }} />
        {isMobile() ? null : config.backButton}
      </Box>
      <Box style={{ marginTop: 0, display: isMobile() ? 'none' : 'block' }}>
        <Typography
          variant={isMobile() ? 'h4' : 'h3'}
          style={{
            fontSize: isDesktop() ? '32px' : '24px',
            marginTop: 64,
            marginBottom: 40,
            fontWeight: 300,
          }}
        >
          {config.header}
        </Typography>
      </Box>
      {!closed
        ? (
          <>
            <Box
              style={{
                height: isMobile() ? window.innerHeight - 140 : 'unset',
                overflowY: isMobile() ? 'auto' : 'unset',
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  style={{
                    fontSize: isMobile() ? '22px' : '24px',
                    fontWeight: 500,
                    marginTop: isMobile() ? 60 : 0,
                    marginBottom: isMobile() ? 16 : 6,
                  }}
                >
                  {config.title}
                </Typography>
                <Typography
                  variant="body1"
                  style={{ fontSize: isMobile() ? '14px' : '18px', color: '#546264' }}
                >
                  {config.description}
                </Typography>
              </Box>
              {isDesktop()
                ? (
                  <Card className={classes.cardBox}>
                    <Typography
                      variant="subtitle1"
                      style={{ fontSize: isMobile() ? '16px' : '20px', weight: 700, marginBottom: 6 }}
                    >
                      {config.reasonTitle}
                    </Typography>
                    <InputField
                      rows={properties.rows}
                      placeholder={properties.placeholder}
                      fullWidth
                      priority={properties.priority}
                      maxLength={properties.maxLength}
                      multiline={properties.multiline}
                      toggle={properties.toggle}
                      type={properties.type}
                      value={reason}
                      onChange={handleChange}
                      classes={{
                        root: classes.limited,
                      }}
                    />
                    {endAdornment}
                  </Card>
                )
                : (
                  <Box className={classes.mobileReason}>
                    <Typography
                      variant="subtitle1"
                      style={{ fontSize: isMobile() ? '16px' : '20px', weight: 700, marginBottom: 12 }}
                    >
                      {config.reasonTitle}
                    </Typography>
                    <InputField
                      onChange={handleChange}
                      classes={{
                        root: classes.limited,
                      }}
                      rows={properties.rows}
                      placeholder={properties.placeholder}
                      fullWidth
                      priority={properties.priority}
                      maxLength={properties.maxLength}
                      multiline={properties.multiline}
                      toggle={properties.toggle}
                      type={properties.type}
                      value={reason}
                    />
                    {endAdornment}
                  </Box>
                )
              }
              <Box className={classes.noticeBox}>
                <Typography
                  variant="body2"
                  style={{ fontSize: isMobile() ? '14px' : '16px', color: '#546264' }}
                >
                  {config.notice}
                </Typography>
              </Box>
            </Box>
            {isSubmitting
              ? (
                <Box className={classes.progress}>
                  <CircularProgress style={{ margin: '0 auto' }} id="closeAccount-CircularProgress" />
                </Box>
              )
              : (
                <Box className={classes.closeButton}>
                  <Button
                    priority="tertiary"
                    name="close-account-button"
                    id="close-account-button"
                    onClick={() => handleClose()}
                  >
                    {config.closeButton}
                  </Button>
                </Box>
              )
            }
          </>
        )
        : (
          <>
            <Box
              className={classes.closeSent}
              style={{
                height: isMobile() ? window.innerHeight - 140 : 'unset',
                overflowY: isMobile() ? 'auto' : 'unset',
                overflowX: 'hidden',
              }}
            >
              <Box className={classes.icon}>
                <img src={successSvg} alt="success-icon" />
              </Box>
              <Box className={classes.title}>
                <Typography
                  variant="h4"
                  style={{
                    fontSize: isDesktop() ? '42px' : '22px',
                    marginBottom: 6,
                    textAlign: 'center',
                    fontWeight: 300,
                    fontFamily: isDesktop() ? 'Montserrat-Light' : 'Montserrat',
                  }}
                >
                  {config.closeAccountSent.title}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                style={{ fontSize: isMobile() ? '14px' : '14px', color: '#546264', textAlign: 'center' }}
              >
                {config.closeAccountSent.description}
              </Typography>
            </Box>
            <Box className={classes.button}>
              <Button
                priority="tertiary"
                name="close-sent-button"
                id="close-sent-button"
                fullWidth
                onClick={
                  () => {
                    dispatch({ currentPage: 'home' });
                    redirectTo({
                      type: 'home', subType: null, hasFooter: false, params: null,
                    });
                  }}
              >
                {config.closeAccountSent.button}
              </Button>
            </Box>
          </>
        )
      }
    </Box>
  );
};

CloseAccount.propTypes = {
  config: object.isRequired,
  redirectTo: func,
  callApi: func,
};

export default CloseAccount;
